import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('portal.drink.select');
import { ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { DrinkView, KioskContext, MeetingContext, MeetingLocation } from "src/libs/portal/kiosk/context";

type DrinkTemperature = 'HOT' | 'ICE';
type IceOption = 'WITH_ICE' | 'NO_ICE';

interface Drink {
    id: string;
    name: string;
    category: string;
    note: string;
    accent: string;
}

interface CartItem {
    id: number;
    drinkId: string;
    drinkName: string;
    category: string;
    temperature: DrinkTemperature;
    iceOption: IceOption | null;
}

@Component({
    selector: 'wiz-portal-drink-select',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/portal.drink.select/view.scss */
:host {
  display: block;
}`],
})
export class PortalDrinkSelectComponent implements OnInit, OnDestroy {
    public selectedDrink: Drink | null = null;
    public selectedTemperature: DrinkTemperature | null = null;
    public selectedIceOption: IceOption | null = null;
    public drinkView: DrinkView = 'menu';
    public timeText: string = '--:--';
    public dateText: string = '';
    public cart: CartItem[] = [];
    public orderedItems: CartItem[] = [];
    public orderCompleted: boolean = false;
    public orderSubmitting: boolean = false;
    public orderSuccessVisible: boolean = false;
    public lastOrderId: string = '';
    public lastOrderLocationLabel: string = '';
    public meetingLocation: MeetingLocation | null = null;
    public meetingContext: MeetingContext = { meetingId: '', reservationId: '', meetingTitle: '' };
    public showLocationSelector: boolean = false;

    private clockTimer: any = null;
    private cartSequence: number = 0;
    private destroyed: boolean = false;

    constructor(@Inject( Service) public service: Service,@Inject( ChangeDetectorRef)  private ref: ChangeDetectorRef) { }

    public drinks: Drink[] = [
        { id: 'water', name: '물', category: 'Water', note: '가볍게 마시는 기본 음료', accent: 'sky' },
        { id: 'americano', name: '아메리카노', category: 'Coffee', note: '진한 향의 기본 커피', accent: 'amber' },
        { id: 'corn-tea', name: '옥수수 수염차', category: 'Tea', note: '고소하고 산뜻한 차', accent: 'yellow' },
        { id: 'barley-tea', name: '보리차', category: 'Tea', note: '부드럽고 편안한 차', accent: 'green' }
    ];

    public async ngOnInit() {
        await this.service.init(null as any);
        this.meetingLocation = KioskContext.loadMeetingLocation();
        this.meetingContext = KioskContext.loadMeetingContext();
        this.showLocationSelector = !this.meetingLocation;
        this.applyInitialView();
        this.updateClock();
        this.clockTimer = setInterval(() => this.updateClock(), 1000 * 30);
    }

    public ngOnDestroy() {
        this.destroyed = true;
        if (this.clockTimer) clearInterval(this.clockTimer);
    }

    public onLocationChange(location: MeetingLocation) {
        this.meetingLocation = location;
        this.showLocationSelector = false;
        this.render();
    }

    public closeLocationSelector() {
        if (!this.meetingLocation) return;

        this.showLocationSelector = false;
        this.render();
    }

    public changeMeetingLocation() {
        this.showLocationSelector = true;
        this.render();
    }

    public goHome() {
        this.service.href('/kiosk/main');
    }

    public selectDrink(drink: Drink) {
        if (!drink) return;

        this.selectedDrink = drink;
        this.selectedTemperature = null;
        this.selectedIceOption = null;
        this.orderCompleted = false;
        this.render();
    }

    public selectTemperature(temperature: DrinkTemperature) {
        this.selectedTemperature = temperature;
        this.selectedIceOption = null;
        this.orderCompleted = false;
        this.render();
    }

    public selectIceOption(option: IceOption) {
        if (this.selectedTemperature !== 'ICE') return;

        this.selectedIceOption = option;
        this.orderCompleted = false;
        this.render();
    }

    public cancelDrinkSelection() {
        this.clearDrinkSelection();
        this.render();
    }

    public goDrinkMenu() {
        this.drinkView = 'menu';
        this.clearDrinkSelection();
        window.history.replaceState({}, '', '/kiosk/drink');
        this.render();
    }

    public goDrinkCart() {
        this.drinkView = 'cart';
        this.clearDrinkSelection();
        window.history.replaceState({}, '', '/kiosk/drink?view=cart');
        this.render();
    }

    public canAddToCart() {
        if (!this.selectedDrink || !this.selectedTemperature) return false;
        if (this.selectedTemperature === 'ICE' && !this.selectedIceOption) return false;

        return true;
    }

    public addToCart() {
        if (!this.canAddToCart() || !this.selectedDrink || !this.selectedTemperature) return;

        this.cart.push({
            id: ++this.cartSequence,
            drinkId: this.selectedDrink.id,
            drinkName: this.selectedDrink.name,
            category: this.selectedDrink.category,
            temperature: this.selectedTemperature,
            iceOption: this.selectedTemperature === 'ICE' ? this.selectedIceOption : null
        });

        this.clearDrinkSelection();
        this.orderCompleted = false;
        this.orderSuccessVisible = false;
        this.render();
    }

    public removeCartItem(item: CartItem) {
        this.cart = this.cart.filter((cartItem) => cartItem.id !== item.id);
        this.render();
    }

    public async submitOrder() {
        if (this.cart.length === 0 || this.orderSubmitting) return;
        const orderLocation = this.resolveOrderLocation();
        if (!orderLocation) {
            this.showLocationSelector = true;
            this.render();
            await this.showError('호실과 장소를 먼저 선택해주세요.', '주문 저장에 실패했습니다.');
            return;
        }

        this.meetingLocation = orderLocation;
        const items = this.cart.map((item) => ({ ...item }));
        const payload = {
            room: orderLocation.room,
            room_name: orderLocation.roomName,
            place: orderLocation.place,
            location_label: orderLocation.label,
            meeting_id: this.meetingContext.meetingId,
            reservation_id: this.meetingContext.reservationId,
            meeting_title: this.meetingContext.meetingTitle,
            items
        };

        this.orderSubmitting = true;
        this.orderSuccessVisible = false;
        this.render();

        try {
            const response: any = await wiz.call('save', {
                data: JSON.stringify(payload)
            });

            if (response?.code !== 200) {
                throw new Error(this.getErrorMessage(response?.data, '주문 저장에 실패했습니다.'));
            }

            this.lastOrderId = response?.data?.id || '';
            this.lastOrderLocationLabel = this.orderLocationLabel(response?.data, orderLocation);
            this.orderedItems = items;
            this.cart = [];
            this.drinkView = 'cart';
            this.clearDrinkSelection();
            this.orderCompleted = true;
            this.orderSuccessVisible = true;
            this.orderSubmitting = false;
            window.history.replaceState({}, '', '/kiosk/drink?view=cart');
            this.render();

            await this.service.sleep(1800);
            if (!this.destroyed) this.service.href('/kiosk/main');
        } catch (error) {
            this.orderSubmitting = false;
            this.orderSuccessVisible = false;
            this.render();
            await this.showError(this.getErrorMessage(error, '주문 저장에 실패했습니다.'), '주문 저장에 실패했습니다.');
        } finally {
            this.orderSubmitting = false;
            this.render();
        }
    }

    public formatCartItem(item: CartItem) {
        const iceText = item.temperature === 'ICE'
            ? item.iceOption === 'WITH_ICE' ? '얼음 있음' : '얼음 없음'
            : '따뜻하게';

        return `${item.drinkName} / ${item.temperature} / ${iceText}`;
    }

    private updateClock() {
        const clock = KioskContext.clockText();
        this.timeText = clock.timeText;
        this.dateText = clock.dateText;
        this.render();
    }

    private render() {
        if (this.destroyed) return;

        setTimeout(() => {
            if (this.destroyed) return;

            this.ref.detectChanges();
            if (this.service && this.service.inited) this.service.render();
        }, 0);
    }

    private clearDrinkSelection() {
        this.selectedDrink = null;
        this.selectedTemperature = null;
        this.selectedIceOption = null;
        this.orderCompleted = false;
    }

    private resolveOrderLocation() {
        const storedLocation = KioskContext.loadMeetingLocation();
        return storedLocation || this.meetingLocation;
    }

    private orderLocationLabel(order: any, fallback: MeetingLocation) {
        if (order?.location_label) return `${order.location_label}`;

        const roomName = order?.room_name || fallback.roomName;
        const place = order?.place || fallback.place;
        return `${roomName} ${place}`.trim();
    }

    private applyInitialView() {
        const params = new URLSearchParams(window.location.search);
        this.drinkView = params.get('view') === 'cart' ? 'cart' : 'menu';
    }

    private getErrorMessage(error: any, fallback: string = '요청에 실패했습니다.') {
        if (!error) return fallback;
        if (typeof error === 'string') return error;
        if (error.message) return error.message;

        return fallback;
    }

    private async showError(message: string, fallback: string = '요청에 실패했습니다.') {
        const errorMessage = message || fallback;
        if (this.service?.modal?.error) {
            await this.service.modal.error(errorMessage);
            return;
        }

        window.alert(errorMessage);
    }
}

export default PortalDrinkSelectComponent;